import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ProductCard from "../productComponent/ProductCard";
import "../css/ProductDetail.css";
import star from "../images/star.png"; // your star icon

const Product = () => {
  const { productId, categoryId } = useParams();
  const navigate = useNavigate();

  // robust user detection: accept either key used in different parts of app
  const user = JSON.parse(sessionStorage.getItem("active-user"));

  // form state
  const [quantity, setQuantity] = useState("");
  const [selectedSize, setSelectedSize] = useState("");

  // data
  const [product, setProduct] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [related, setRelated] = useState([]);

  // reviews
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState("0.0");
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 5;

  const CART_KEY = "cart";

  const readCart = () => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  const writeCart = (items) => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
      window.dispatchEvent(new Event("storage"));
    } catch {}
  };

  const addToLocalCart = ({ product, categorySizeId = null, quantity }) => {
    const items = readCart();
    const productId = String(product.id);
    const unitPrice = Number(product.price) || 0;
    const qty = Number(quantity);

    const idx = items.findIndex(
      (it) =>
        String(it.productId) === productId &&
        String(it.categorySizeId) === String(categorySizeId)
    );

    if (idx > -1) {
      items[idx].quantity = Number(items[idx].quantity) + qty;
      items[idx].totalPrice = items[idx].quantity * items[idx].price;
    } else {
      const selectedSizeObj = sizes.find(
        (s) => String(s.id) === String(categorySizeId)
      );

      items.push({
        quantity: qty,
        userId: null,
        productId,
        categoryId: product.category?.id || "",
        categoryName: product.category?.title || "",
        categorySizeId: categorySizeId === null ? null : String(categorySizeId),
        size: selectedSizeObj ? selectedSizeObj.size : "",
        price: unitPrice,
        totalPrice: qty * unitPrice,
        productName: product.title || "",
        productImage: product.imageName || "",
      });
    }

    writeCart(items);
  };

  // fetch product, related products & reviews
  useEffect(() => {
    const fetchData = async () => {
      try {
        // product
        const resp = await axios.get(
          `https://goldenhides.in/api/product/id?productId=${productId}`
        );
        if (resp.data && resp.data.products && resp.data.products[0]) {
          const prod = resp.data.products[0];
          setProduct(prod);
          if (prod.category?.sizes) setSizes(prod.category.sizes);
        }

        // related by category
        if (categoryId) {
          const rel = await axios.get(
            `https://goldenhides.in/api/product/category?categoryId=${categoryId}`
          );
          if (rel.data && rel.data.products) setRelated(rel.data.products);
        }

        // reviews
        const rev = await axios.get(
          `https://goldenhides.in/api/product/review/fetch?productId=${productId}`
        );
        if (rev.data) {
          setReviews(rev.data.reviews || []);
          setRating(rev.data.averageRating || "0.0");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load product or reviews");
      }
    };

    fetchData();
  }, [productId, categoryId]);

  // pagination for reviews
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);

  const nextPage = () => {
    if (indexOfLastReview < reviews.length) setCurrentPage((p) => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  // Add to cart
  const addToCart = (e) => {
    e.preventDefault();

    if (!selectedSize) {
      toast.info("Please select a size!", { position: "top-center" });
      return;
    }

    if (!quantity || Number(quantity) <= 0) {
      toast.info("Please enter a valid quantity!", { position: "top-center" });
      return;
    }

    const qty = Number(quantity);

    if (!user) {
      addToLocalCart({
        product,
        categorySizeId: selectedSize,
        quantity: qty,
      });
      toast.success("Added to Cart", {
        position: "top-center",
        autoClose: 1200,
      });
      setQuantity("");
      setSelectedSize("");
      return;
    }

    // logged-in flow
    fetch("https://goldenhides.in/api/user/cart/add", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        quantity: qty,
        userId: user.id,
        productId: productId,
        categorySizeId: selectedSize,
      }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          toast.success(res.responseMessage, { position: "top-center" });
          setTimeout(() => navigate("/user/mycart"), 900);
        } else {
          toast.error(res.responseMessage, { position: "top-center" });
        }
      })
      .catch(() => toast.error("Server error"));
  };

  if (!product) {
    return <div className="container py-5">Loading...</div>;
  }

  const images = [
    product.imageName,
    product.imageName2,
    product.imageName3,
    product.imageName4,
  ].filter(Boolean);

  // navigate to AddReview page (pass current product as state)
  const handleWriteReview = () => {
    navigate(`/product/${productId}/review/add`, { state: product });
  };

  return (
    <div className="product-page container-fluid px-4 my-4">
      <div className="row gx-4">
        {/* left: images */}
        <div className="col-lg-7">
          <div className="image-grid">
            {images.slice(0, 4).map((img, i) => (
              <div key={i} className={`grid-item item-${i + 1}`}>
                <img
                  src={`https://goldenhides.in/api/product/${img}`}
                  alt={product.title}
                  className="prod-image"
                />
              </div>
            ))}
          </div>
        </div>

        {/* right: info */}
        <div className="col-lg-5">
          <div className="product-info-box p-3">
            <h2 className="prod-title">{product.title}</h2>
            <p className="prod-desc">{product.description}</p>

            <div className="price-row d-flex align-items-baseline gap-3">
              <div className="current-price">
                ₹{Number(product.price).toFixed(2)}
              </div>
            </div>

            <div className="mt-4">
              <label className="form-label fw-bold">Select Size</label>
              <div className="size-options d-flex flex-wrap gap-2 mt-2">
                {sizes.length === 0 ? (
                  <div className="text-muted small">No sizes available</div>
                ) : (
                  sizes.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`size-btn ${
                        String(selectedSize) === String(s.id) ? "active" : ""
                      }`}
                      onClick={() => setSelectedSize(s.id)}
                    >
                      {s.size}
                    </button>
                  ))
                )}
              </div>
            </div>

            <form className="mt-3" onSubmit={addToCart}>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="number"
                  min="1"
                  className="form-control quantity-input"
                  placeholder="Quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <button type="submit" className="btn add-btn">
                  Add to Bag
                </button>
              </div>
              <div className="mt-2">
                <small className="text-muted">Stock: {product.quantity}</small>
              </div>
            </form>

            {/* Reviews Section */}
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2 justify-content-between">
                <div>
                  <h5 className="mb-0">Customer Reviews</h5>
                  <span className="ms-2 fw-bold text-success">
                    {rating}{" "}
                    <img
                      src={star}
                      width="18"
                      height="18"
                      alt="rating"
                      className="d-inline-block"
                    />
                  </span>
                </div>

                {/* show Write Review only to logged-in customer */}
                {user && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleWriteReview}
                    >
                      Write Review
                    </button>
                  </div>
                )}
              </div>

              {currentReviews.length > 0 ? (
                <>
                  {currentReviews.map((review, idx) => (
                    <div
                      key={idx}
                      className="border rounded p-2 mb-2"
                      style={{ background: "#f9f9f9" }}
                    >
                      <div className="d-flex justify-content-between">
                        <b>
                          {review.user?.firstName} {review.user?.lastName}
                        </b>
                        <span className="text-warning fw-bold">
                          {review.star}/5 ⭐
                        </span>
                      </div>
                      <p className="mt-1 mb-0 small">{review.review}</p>
                    </div>
                  ))}

                  <div className="d-flex justify-content-between mt-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      ← Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={indexOfLastReview >= reviews.length}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-muted">No reviews yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* related */}
      <div className="row mt-5">
        <div className="col-12">
          <h5>Related Products</h5>
          <div className="row row-cols-2 row-cols-md-6 g-3 mt-3">
            {related.map((p) => (
              <ProductCard key={p.id} item={p} />
            ))}
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Product;
